import type { ListBuilderConfiguration } from '../types';

/**
 * Shared column configuration for item image
 */
const getImageColumn = () => ({
	id: 'image',
	width: 60,
	fields: [
		{
			id: 'item_image',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'primaryImageUrl', objectTable: 'item' },
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
				imageOptions: {
					size: 'small' as const,
					enableViewer: true,
				},
			},
			displayStyle: 'image' as const,
		},
	],
	headers: [{ id: 'image_header', text: '' }],
	sortable: false,
	alignment: 'center' as const,
	verticalAlignment: 'middle' as const,
});

/**
 * Shared column configuration for item info
 */
const getItemInfoColumn = () => ({
	id: 'item-info',
	label: 'Item',
	width: 250,
	fields: [
		{
			id: 'name-field',
			link: {
				type: 'object' as const,
				enabled: true,
				objectTable: 'item',
			},
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'description', objectTable: 'item' },
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'text' as const,
		},
		{
			id: 'lb1',
			dataConfig: { type: 'custom_text' as const, customText: '' },
			styleConfig: {
				size: 'md' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'line_break' as const,
		},
		{
			id: 'manufacturer-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'manufacturer', objectTable: 'item' },
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
			},
			displayStyle: 'text' as const,
		},
		{
			id: 'lb2',
			dataConfig: { type: 'custom_text' as const, customText: '' },
			styleConfig: {
				size: 'md' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'line_break' as const,
		},
		{
			id: 'sku-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'externalId2', objectTable: 'item' },
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
			},
			displayStyle: 'text' as const,
		},
		{
			id: 'item_attribute_icons',
			dataConfig: { type: 'custom_text' as const, customText: '' },
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'custom' as const,
			customComponent: { id: 'shop-item-attribute-icons' },
		},
	],
	headers: [{ id: 'h1', text: 'Item' }],
	minWidth: 200,
	sortable: true,
	alignment: 'left' as const,
	sortFields: [
		{
			direction: 'ASC' as const,
			fieldName: 'description',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top' as const,
});

/**
 * Shared column configuration for category
 */
const getCategoryColumn = () => ({
	id: 'category',
	label: 'Category',
	width: 180,
	fields: [
		{
			id: 'category',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'category', objectTable: 'item' },
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'text' as const,
		},
	],
	headers: [{ id: 'h2', text: 'Category' }],
	sortable: true,
	alignment: 'left' as const,
	sortFields: [
		{
			direction: 'ASC' as const,
			fieldName: 'category',
			objectTable: 'item',
			nullsLast: true,
		},
	],
	verticalAlignment: 'top' as const,
});

/**
 * Shared column configuration for quantity
 */
const getQuantityColumn = () => ({
	id: 'quantity',
	label: 'On Hand',
	width: 80,
	fields: [
		{
			id: 'quantity-value',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'quantityOnHand', objectTable: 'item' },
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'text' as const,
		},
	],
	headers: [{ id: 'h5', text: 'On Hand' }],
	sortable: true,
	alignment: 'center' as const,
	sortFields: [
		{
			direction: 'DESC' as const,
			fieldName: 'quantityOnHand',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top' as const,
});

/**
 * Restocked date column — shown on the "Recently Restocked" tab.
 * Sortable by lastRestockedDate. Direction is overridden at runtime by
 * the tab's defaultSortId (`column-restocked_date-desc`).
 */
const getRestockedDateColumn = () => ({
	id: 'restocked_date',
	label: 'Restocked',
	width: 110,
	fields: [
		{
			id: 'restocked-date-value',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'lastRestockedDate', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'date' as const,
			dateOptions: { format: 'medium' as const },
		},
	],
	headers: [{ id: 'restocked-date-header', text: 'Restocked' }],
	sortable: true,
	alignment: 'right' as const,
	sortFields: [
		{
			direction: 'DESC' as const,
			fieldName: 'lastRestockedDate',
			objectTable: 'item',
			nullsLast: true,
		},
	],
	verticalAlignment: 'top' as const,
});

/**
 * Computed created date column — shown on the "Newly Stocked" tab. Prefers
 * externalCreatedDate (the date from the source ERP), falls back to the
 * item record's _created_date when unset. Sort applies both fields in
 * order, the closest the list-builder framework gets to COALESCE ordering
 * — the filter itself uses a real COALESCE via dateExpression so the row
 * set matches exactly, only sort within that set is the multi-field
 * approximation.
 */
const getComputedCreatedDateColumn = () => ({
	id: 'created_date',
	label: 'Added',
	width: 110,
	fields: [
		{
			id: 'created-date-value',
			dataConfig: {
				type: 'conditional' as const,
				conditions: [
					{
						field: 'externalCreatedDate',
						operator: 'not_equals' as const,
						value: null,
						dataConfig: {
							type: 'field' as const,
							field: {
								fieldName: 'externalCreatedDate',
								objectTable: 'item',
							},
						},
					},
				],
				defaultDataConfig: {
					type: 'field' as const,
					field: { fieldName: '_created_date', objectTable: 'item' },
					defaultValue: '-',
				},
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'date' as const,
			dateOptions: { format: 'medium' as const },
		},
	],
	headers: [{ id: 'created-date-header', text: 'Added' }],
	sortable: true,
	alignment: 'right' as const,
	sortFields: [
		{
			direction: 'DESC' as const,
			fieldName: 'externalCreatedDate',
			objectTable: 'item',
			nullsLast: true,
		},
		{
			direction: 'DESC' as const,
			fieldName: '_created_date',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top' as const,
});

/**
 * Shared column configuration for item actions
 * The removeItem action only appears when a removeItem callback is provided
 */
const getActionsColumn = () => ({
	id: 'actions',
	fields: [
		{
			id: 'actions-dropdown',
			dataConfig: {
				type: 'custom_text' as const,
				customText: '',
			},
			styleConfig: {
				size: 'md' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'actions_dropdown' as const,
			dropdownActions: [
				{
					id: 'remove-item-action',
					label: 'Remove from Group',
					iconName: 'trash',
					callbackName: 'removeItem',
					variant: 'danger',
					visibility: 'visible',
				},
			],
		},
	],
	headers: [{ id: 'actions-header', text: '' }],
	sortable: false,
	alignment: 'center' as const,
	widthConfig: {
		mode: 'fixed' as const,
		width: 80,
	},
	verticalAlignment: 'top' as const,
});

/**
 * Item list configuration
 * Main items list with multi-view support (All Items and Active Items)
 * Converted from JSON to TypeScript for type safety
 */
export const ITEM_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-items-all',
			cacheTTL: 600000,
			name: 'All Items',
			type: 'default',
			active: true,
			columns: [
				getImageColumn(),
				getItemInfoColumn(),
				getCategoryColumn(),
				getQuantityColumn(),
				getActionsColumn(),
			],
			description: 'Complete inventory list with all items',
			defaultSortId: 'column-item-info-asc',
			sortOrder: 0,
		},
		{
			id: 'default-items-active',
			cacheTTL: 600000,
			name: 'Active Items',
			type: 'default',
			active: true,
			columns: [
				getImageColumn(),
				getItemInfoColumn(),
				getCategoryColumn(),
				getQuantityColumn(),
				getActionsColumn(),
			],
			description: 'Only active items',
			defaultSortId: 'column-item-info-asc',
			sortOrder: 1,
			filterRules: {
				itemStatus: {
					defaultValue: [{ id: 'Active', label: 'Active' }],
				},
			},
		},
		{
			id: 'default-items-recently-restocked',
			cacheTTL: 600000,
			name: 'Recently Restocked',
			type: 'default',
			active: true,
			columns: [
				getImageColumn(),
				getItemInfoColumn(),
				getCategoryColumn(),
				getQuantityColumn(),
				getRestockedDateColumn(),
				getActionsColumn(),
			],
			description:
				'Items whose lastRestockedDate is within the last 30 days. Mirrors the client shop "Recently Restocked" tab.',
			defaultSortId: 'column-restocked_date-desc',
			sortOrder: 2,
			filterRules: {
				itemRestockedDateRange: {
					defaultValue: [
						{
							id: 'last-30-days',
							label: 'Last 30 Days',
							data: { startDate: '{{DATE:-30d}}', endDate: '{{DATE:0d}}' },
						},
					],
				},
			},
		},
		{
			id: 'default-items-newly-stocked',
			cacheTTL: 600000,
			name: 'Newly Stocked',
			type: 'default',
			active: true,
			columns: [
				getImageColumn(),
				getItemInfoColumn(),
				getCategoryColumn(),
				getQuantityColumn(),
				getComputedCreatedDateColumn(),
				getActionsColumn(),
			],
			description:
				'Items created in the last 30 days, using COALESCE(externalCreatedDate, _created_date) so the result matches the client-shop "Newly Stocked" tab.',
			defaultSortId: 'column-created_date-desc',
			sortOrder: 3,
			filterRules: {
				itemCreatedDateRange: {
					defaultValue: [
						{
							id: 'last-30-days',
							label: 'Last 30 Days',
							data: { startDate: '{{DATE:-30d}}', endDate: '{{DATE:0d}}' },
						},
					],
				},
			},
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100, 200],
	},
	filters: {
		activeFilters: [
			'itemSearch',
			'itemCompany',
			'itemCategories',
			'itemManufacturers',
			'itemGroups',
			'itemStatus',
			'itemShortDate',
			'stock_status',
			'itemRestockedDateRange',
			'itemCreatedDateRange',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'externalId2', fieldLabel: 'SKU' },
				{
					fieldName: 'extendedDescription',
					fieldLabel: 'Extended Description',
				},
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				{ fieldName: 'manufacturerItemId', fieldLabel: 'Manufacturer Item ID' },
				{ fieldName: 'brandEquivalent', fieldLabel: 'Brand Equivalent' },
				{ fieldName: 'category', fieldLabel: 'Category' },
				{ fieldName: 'subcategory', fieldLabel: 'Subcategory' },
				{ fieldName: 'sizeCount', fieldLabel: 'Size Count' },
				{ fieldName: 'unitOfMeasure', fieldLabel: 'Unit of Measure' },
				{ fieldName: 'strength', fieldLabel: 'Strength' },
				{ fieldName: 'countryOfOrigin', fieldLabel: 'Country of Origin' },
				{ fieldName: 'family', fieldLabel: 'Family' },
				{ fieldName: 'storageType', fieldLabel: 'Storage Type' },
				{ fieldName: 'tradeStyle', fieldLabel: 'Trade Style' },
				{ fieldName: 'isActive', fieldLabel: 'Active' },
				{
					fieldName: 'ecommerceVisibility',
					fieldLabel: 'Ecommerce Visibility',
				},
				{ fieldName: 'price', fieldLabel: 'Price' },
				{ fieldName: 'minimumPrice', fieldLabel: 'Minimum Price' },
				{ fieldName: 'previousPrice', fieldLabel: 'Previous Price' },
				{
					fieldName: 'priceModifiedDatetime',
					fieldLabel: 'Price Modified Date',
				},
				{ fieldName: 'acquisitionCost', fieldLabel: 'Acquisition Cost' },
				{ fieldName: 'sellCost', fieldLabel: 'Sell Cost' },
				{ fieldName: 'averageCost', fieldLabel: 'Average Cost' },
				{
					fieldName: 'averageWholesalePrice',
					fieldLabel: 'Average Wholesale Price',
				},
				{ fieldName: 'quantityOnHand', fieldLabel: 'Quantity On Hand' },
				{ fieldName: 'quantityOnOrder', fieldLabel: 'Quantity On Order' },
				{
					fieldName: 'quantityOnBackorder',
					fieldLabel: 'Quantity On Backorder',
				},
				{ fieldName: 'lastImportDate', fieldLabel: 'Last Import Date' },
				{
					fieldName: 'externalCreatedDate',
					fieldLabel: 'External Created Date',
				},
				{ fieldName: 'soonestArrivalDate', fieldLabel: 'Soonest Arrival Date' },
				{
					fieldName: 'soonestExpirationDate',
					fieldLabel: 'Soonest Expiration Date',
				},
				{
					fieldName: 'lastOutOfStockDate',
					fieldLabel: 'Last Out of Stock Date',
				},
				{ fieldName: 'upc', fieldLabel: 'UPC' },
				{ fieldName: 'caseUpc', fieldLabel: 'Case UPC' },
				{ fieldName: 'retailUpc', fieldLabel: 'Retail UPC' },
				{ fieldName: 'extraUpc', fieldLabel: 'Extra UPC' },
				{ fieldName: 'ndc', fieldLabel: 'NDC' },
				{ fieldName: 'standardNdc', fieldLabel: 'Standard NDC' },
				{ fieldName: 'isShortDate', fieldLabel: 'Short Date' },
				{ fieldName: 'isHazmat', fieldLabel: 'Hazmat' },
				{ fieldName: 'isRefrigerated', fieldLabel: 'Refrigerated' },
				{ fieldName: 'isHeavy', fieldLabel: 'Heavy' },
				{ fieldName: 'caseSize', fieldLabel: 'Case Size' },
				{ fieldName: 'unitsPerPallet', fieldLabel: 'Units Per Pallet' },
				{ fieldName: 'weight', fieldLabel: 'Weight' },
				{ fieldName: 'length', fieldLabel: 'Length' },
				{ fieldName: 'width', fieldLabel: 'Width' },
				{ fieldName: 'height', fieldLabel: 'Height' },
				{ fieldName: 'dailyLimit', fieldLabel: 'Daily Limit' },
				{ fieldName: 'perOrderLimit', fieldLabel: 'Per Order Limit' },
				{ fieldName: 'conversionFactor', fieldLabel: 'Conversion Factor' },
				{ fieldName: 'invoiceFrequency', fieldLabel: 'Invoice Frequency' },
				{
					fieldName: 'soldUnitsAggregate',
					fieldLabel: '3-Month Sold Units',
				},
				{ fieldName: 'revenueAggregate', fieldLabel: '3-Month Revenue' },
				{
					fieldName: 'uniqueClientsAggregate',
					fieldLabel: '3-Month Unique Clients',
				},
				{
					fieldName: 'uniqueInvoicesAggregate',
					fieldLabel: '3-Month Unique Invoices',
				},
				{ fieldName: 'soldUnitsMtd', fieldLabel: 'Sold Units MTD' },
				{
					fieldName: 'soldUnitsLastMonth',
					fieldLabel: 'Sold Units Last Month',
				},
				{
					fieldName: 'soldUnitsTwoMonthsAgo',
					fieldLabel: 'Sold Units Two Months Ago',
				},
				{ fieldName: 'revenueMtd', fieldLabel: 'Revenue MTD' },
				{ fieldName: 'revenueLastMonth', fieldLabel: 'Revenue Last Month' },
				{
					fieldName: 'revenueTwoMonthsAgo',
					fieldLabel: 'Revenue Two Months Ago',
				},
				{ fieldName: 'uniqueClientsMtd', fieldLabel: 'Unique Clients MTD' },
				{
					fieldName: 'uniqueClientsLastMonth',
					fieldLabel: 'Unique Clients Last Month',
				},
				{
					fieldName: 'uniqueClientsTwoMonthsAgo',
					fieldLabel: 'Unique Clients Two Months Ago',
				},
				{ fieldName: 'uniqueInvoicesMtd', fieldLabel: 'Unique Invoices MTD' },
				{
					fieldName: 'uniqueInvoicesLastMonth',
					fieldLabel: 'Unique Invoices Last Month',
				},
				{
					fieldName: 'uniqueInvoicesTwoMonthsAgo',
					fieldLabel: 'Unique Invoices Two Months Ago',
				},
				{ fieldName: 'totalGpAggregate', fieldLabel: 'Total GP (Aggregate)' },
				{ fieldName: 'totalGpMargin', fieldLabel: 'Total GP Margin' },
				{
					fieldName: 'rollingTotalGpAggregate30',
					fieldLabel: 'Rolling GP (30 Days)',
				},
				{
					fieldName: 'rollingTotalGpAggregate90',
					fieldLabel: 'Rolling GP (90 Days)',
				},
				{
					fieldName: 'rollingTotalGpMargin30',
					fieldLabel: 'Rolling GP Margin (30 Days)',
				},
				{
					fieldName: 'rollingTotalGpMargin90',
					fieldLabel: 'Rolling GP Margin (90 Days)',
				},
				{
					fieldName: 'rollingRevenue30',
					fieldLabel: 'Rolling Revenue (30 Days)',
				},
				{
					fieldName: 'rollingRevenue90',
					fieldLabel: 'Rolling Revenue (90 Days)',
				},
				{
					fieldName: 'rollingTotalGpAggregateTrend30',
					fieldLabel: 'Rolling GP Trend (30 Days)',
				},
				{
					fieldName: 'rollingTotalGpAggregateTrend90',
					fieldLabel: 'Rolling GP Trend (90 Days)',
				},
				{
					fieldName: 'rollingTotalGpMarginTrend30',
					fieldLabel: 'Rolling GP Margin Trend (30 Days)',
				},
				{
					fieldName: 'rollingTotalGpMarginTrend90',
					fieldLabel: 'Rolling GP Margin Trend (90 Days)',
				},
				{
					fieldName: 'rollingRevenueTrend30',
					fieldLabel: 'Rolling Revenue Trend (30 Days)',
				},
				{
					fieldName: 'rollingRevenueTrend90',
					fieldLabel: 'Rolling Revenue Trend (90 Days)',
				},
				{ fieldName: 'createdDate', fieldLabel: 'Created Date' },
				{ fieldName: 'modifiedDate', fieldLabel: 'Modified Date' },
				{ fieldName: 'ownerUserId', fieldLabel: 'Owner User ID' },
				{ fieldName: 'creatorUserId', fieldLabel: 'Creator User ID' },
			],
		},
	],
	tableSorts: [
		{
			id: 'relevance',
			name: 'Relevance',
			fields: [
				{ direction: 'DESC', fieldName: 'relevance', objectTable: 'item' },
			],
			description: 'Sort by search relevance',
		},
		{
			id: 'name',
			name: 'Name',
			fields: [
				{ direction: 'ASC', fieldName: 'description', objectTable: 'item' },
			],
			description: 'Sort items by name',
		},
		{
			id: 'price',
			name: 'Price',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'price',
					objectTable: 'item',
					nullsLast: true,
				},
			],
			description: 'Sort items by price',
		},
		{
			id: 'category',
			name: 'Category',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'category',
					objectTable: 'item',
					nullsLast: true,
				},
				{
					direction: 'ASC',
					fieldName: 'subcategory',
					objectTable: 'item',
					nullsLast: true,
				},
			],
			description: 'Sort by category then subcategory',
		},
		{
			id: 'manufacturer',
			name: 'Manufacturer',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'manufacturer',
					objectTable: 'item',
					nullsLast: true,
				},
			],
			description: 'Sort by manufacturer name',
		},
		{
			id: 'stock',
			name: 'Stock Level',
			fields: [
				{ direction: 'DESC', fieldName: 'quantityOnHand', objectTable: 'item' },
			],
			description: 'Sort by quantity on hand',
		},
	],
	importExport: {
		allowExport: true,
		allowImport: true,
		exportFields: [
			'description',
			'externalId',
			'manufacturer',
			'price',
			'category',
			'subcategory',
			'unitOfMeasure',
			'isActive',
			'quantityOnHand',
			'primaryImageUrl',
		],
		importFields: [
			'description',
			'externalId',
			'manufacturer',
			'price',
			'category',
			'subcategory',
			'unitOfMeasure',
			'isActive',
		],
	},
};
